import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

const PH = 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=120&q=60';
const resImg = ph => !ph ? PH : ph.startsWith('http') ? ph : `https://shop.venkatagirihandlooms.com/${ph.replace(/^\/+/,'')}`;

// ── RAZORPAY KEY — replace with your live key ─────────────────
const RAZORPAY_KEY = 'rzp_test_YourKeyHere';

// ── COUPON SUGGESTIONS ─────────────────────────────────────────
// Coupons are loaded from DB — see useCoupons() below

// ── ADDRESS FIELDS CONFIG ──────────────────────────────────────
const ADDR_FIELDS = [
  { name:'name',     label:'Full Name',    placeholder:'As per Govt ID',         required:true,  half:true },
  { name:'phone',    label:'Phone Number', placeholder:'+91 XXXXX XXXXX (10 digits)', required:true, half:true },
  { name:'address',  label:'Address',      placeholder:'Flat/House No, Street, Area',required:true,  half:false, multiline:true },
  { name:'landmark', label:'Landmark',     placeholder:'Near temple, school… (optional)', required:false, half:false },
  { name:'city',     label:'City',         placeholder:'City',                   required:true,  half:true },
  { name:'pincode',  label:'Pincode',      placeholder:'6-digit pincode',        required:true,  half:true },
  { name:'state',    label:'State',        placeholder:'',                       required:true,  half:false, select:true },
];
const STATES = ['Andhra Pradesh','Telangana','Tamil Nadu','Karnataka','Kerala','Maharashtra','Delhi','Gujarat','Rajasthan','West Bengal','Bihar','Odisha','Madhya Pradesh','Uttar Pradesh','Himachal Pradesh','Punjab','Haryana','Other'];

export default function CheckoutPage() {
  const { user }         = useAuth();
  const { cart, clearCart } = useCart();
  const navigate         = useNavigate();

  const [step,     setStep]     = useState(1);   // 1=Address, 2=Payment, 3=Success
  const [loading,  setLoading]  = useState(false);
  const [orderId,  setOrderId]  = useState('');
  const [errors,   setErrors]   = useState({});

  // Address
  const [address, setAddress] = useState({
    name:     user ? `${user.first_name} ${user.last_name||''}`.trim() : '',
    phone:    user?.phone || '',
    address:  '', landmark: '', city: '', state: 'Andhra Pradesh', pincode: ''
  });

  // Payment
  const [payment, setPayment] = useState('COD');
  const [giftWrap, setGiftWrap] = useState(false);
  const [giftMessage, setGiftMessage] = useState('');

  // Coupon
  const [couponCode,    setCouponCode]    = useState('');
  const [couponInput,   setCouponInput]   = useState('');
  const [couponData,    setCouponData]    = useState(null);   // {discount, type, description, free_shipping}
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError,   setCouponError]   = useState('');
  const [showCoupons,   setShowCoupons]   = useState(false);
  const [activeCoupons, setActiveCoupons] = useState([]);
  const [couponsLoading, setCouponsLoading] = useState(false);

  // Razorpay script loader
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => document.body.removeChild(script);
  }, []);

  if (!user) return (
    <div className="page" style={{textAlign:'center',paddingTop:120}}>
      <h2 style={{fontFamily:"'Playfair Display',serif",color:'#7a1f1f',fontStyle:'italic'}}>Please Login</h2>
      <Link to="/login" className="btn btn-primary" style={{marginTop:20}}>Login</Link>
    </div>
  );
  if (!cart.items.length && step !== 3) { navigate('/cart'); return null; }

  // ── Price calculations ─────────────────────────────────────────
  const subtotal       = cart.total;
  const couponDiscount = couponData?.discount || 0;
  const freeShip       = couponData?.free_shipping || false;
  const afterCoupon    = Math.max(0, subtotal - couponDiscount);
  const giftWrapFee    = giftWrap ? 99 : 0;
  const shipping       = freeShip ? 0 : afterCoupon >= 999 ? 0 : 99;
  const grandTotal     = afterCoupon + shipping + giftWrapFee;

  // ── Address validation ─────────────────────────────────────────
  const validateAddress = () => {
    const e = {};
    if (!address.name.trim())    e.name    = 'Full name is required';
    if (!address.phone.trim())   e.phone   = 'Phone number is required';
    else if (!/^\d{10}$/.test(address.phone.replace(/\D/g,''))) e.phone = 'Enter valid 10-digit number';
    if (!address.address.trim()) e.address = 'Address is required';
    if (!address.city.trim())    e.city    = 'City is required';
    if (!address.state.trim())   e.state   = 'State is required';
    if (!address.pincode.trim()) e.pincode = 'Pincode is required';
    else if (!/^\d{6}$/.test(address.pincode)) e.pincode = 'Enter valid 6-digit pincode';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => { if (validateAddress()) setStep(2); else toast.error('Please fill all required fields correctly'); };

  // ── Coupon apply ───────────────────────────────────────────────
  const applyCoupon = async (code) => {
    const c = (code || couponInput).trim().toUpperCase();
    if (!c) return;
    setCouponLoading(true);
    setCouponError('');
    try {
      const res = await api.post('/coupons', { code: c, cart_total: subtotal });
      setCouponData(res.data);
      setCouponCode(c);
      setCouponInput(c);
      setShowCoupons(false);
      toast.success(`Coupon applied! ${res.data.description}`);
    } catch (err) {
      setCouponError(err.message);
      setCouponData(null);
      setCouponCode('');
      toast.error(err.message);
    } finally { setCouponLoading(false); }
  };

  const removeCoupon = () => { setCouponData(null); setCouponCode(''); setCouponInput(''); setCouponError(''); toast.success('Coupon removed'); };

  // Fetch active coupons from DB when user opens the panel
  const loadActiveCoupons = async () => {
    if (activeCoupons.length > 0) return; // already loaded
    setCouponsLoading(true);
    try {
      const res = await api.get('/coupons');
      setActiveCoupons(res.data || []);
    } catch {
      // silently fail — user can still type manually
    } finally {
      setCouponsLoading(false);
    }
  };

  // ── Place order (COD / net banking / etc.) ─────────────────────
  const placeOrder = async (extraPayload = {}) => {
    setLoading(true);
    try {
      const res = await api.post('/orders', {
        address,
        payment_method:      payment,
        coupon_code:         couponCode,
        coupon_discount:     couponDiscount,
        gift_wrap:           giftWrap,
        gift_message:        giftMessage,
        ...extraPayload,
      });
      setOrderId(res.data.order_id);
      setStep(3);
      toast.success('Order placed successfully! 🎉');
    } catch (err) {
      toast.error(err.message);
    } finally { setLoading(false); }
  };

  // ── Razorpay payment flow ──────────────────────────────────────
  const handleRazorpay = async () => {
    if (!window.Razorpay) { toast.error('Payment gateway not loaded. Please refresh.'); return; }
    setLoading(true);
    try {
      const options = {
        key:           RAZORPAY_KEY,
        amount:        Math.round(grandTotal * 100),   // paise
        currency:      'INR',
        name:          'Venkatagiri Handlooms',
        description:   `Order — ${cart.count} saree${cart.count!==1?'s':''}`,
        image:         'https://venkatagirihandlooms.com/logo192.png',
        prefill: {
          name:    address.name,
          contact: address.phone,
          email:   user.email,
        },
        notes: {
          customer_id: user.id,
          coupon_code: couponCode || 'NONE',
        },
        theme: { color: '#7a1f1f' },
        handler: async (response) => {
          // Payment successful — create order with Razorpay ID
          await placeOrder({
            payment_method:       'RAZORPAY',
            razorpay_payment_id:  response.razorpay_payment_id,
          });
        },
        modal: {
          ondismiss: () => { setLoading(false); toast('Payment cancelled'); }
        }
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        setLoading(false);
        toast.error(`Payment failed: ${resp.error.description}`);
      });
      rzp.open();
    } catch (err) {
      setLoading(false);
      toast.error(err.message);
    }
  };

  // ── Submit handler ─────────────────────────────────────────────
  const handlePlaceOrder = async () => {
    try {
      if (payment === 'RAZORPAY' || payment === 'CARD' || payment === 'UPI') {
        await handleRazorpay();
      } else {
        await placeOrder();
      }
    } catch (e) {
      setLoading(false);
      toast.error('Something went wrong. Please try again.');
    }
  };

  // ── Sub-components ─────────────────────────────────────────────
  const inp = (err) => ({
    width:'100%', padding:'11px 13px',
    border:`1.5px solid ${err ? '#c00' : 'var(--border)'}`,
    background: err ? '#fff5f5' : '#fff',
    fontSize:14, outline:'none', borderRadius:0, WebkitAppearance:'none',
    fontFamily:'inherit',
  });

  const FieldErr = ({name}) => errors[name] ? <div style={{color:'#c00',fontSize:11,marginTop:4,fontFamily:"'Jost',sans-serif"}}>⚠ {errors[name]}</div> : null;

  const OrderSummary = ({mobile=false}) => (
    <div style={{background:'#fff',border:'1px solid var(--border)',padding:22,...(mobile?{}:{position:'sticky',top:'calc(var(--nav-h)+16px)'})}}>
      <h3 style={{fontFamily:"'Playfair Display',serif",fontSize:19,color:'#1a0f0f',fontStyle:'italic',marginBottom:18}}>Order Summary</h3>

      {/* Cart items */}
      <div style={{maxHeight:240,overflowY:'auto',marginBottom:16}}>
        {cart.items.map(item => (
          <div key={item.id} style={{display:'flex',alignItems:'flex-start',gap:10,marginBottom:12,paddingBottom:12,borderBottom:'1px solid #f0e8e0'}}>
            <img src={resImg(item.photo)} alt={item.name} style={{width:46,height:58,objectFit:'cover',flexShrink:0,background:'#f7f0e6'}} onError={e=>{e.target.src=PH;}}/>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:14,lineHeight:1.3,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{item.name}</div>
              <div style={{fontSize:11,color:'#8a7968'}}>Qty: {item.qty}</div>
            </div>
            <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:15,color:'#7a1f1f',fontWeight:600,flexShrink:0}}>₹{Number(item.subtotal).toLocaleString('en-IN',{maximumFractionDigits:0})}</div>
          </div>
        ))}
      </div>

      {/* Coupon box */}
      {step === 1 && (
        <div style={{marginBottom:16}}>
          {couponData ? (
            <div style={{background:'#e8f5e9',border:'1px solid #81c784',padding:'10px 14px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div>
                <div style={{fontFamily:"'Jost',sans-serif",fontSize:11,fontWeight:700,color:'#2e7d32',letterSpacing:1}}>✅ {couponCode}</div>
                <div style={{fontSize:11,color:'#388e3c',marginTop:2}}>You save ₹{couponDiscount.toLocaleString('en-IN',{maximumFractionDigits:0})}</div>
              </div>
              <button onClick={removeCoupon} style={{fontSize:12,color:'#c00',background:'none',border:'none',cursor:'pointer',fontFamily:"'Jost',sans-serif",textDecoration:'underline'}}>Remove</button>
            </div>
          ) : (
            <div>
              <div style={{display:'flex',gap:8,marginBottom:6}}>
                <input value={couponInput} onChange={e=>setCouponInput(e.target.value.toUpperCase())} onKeyDown={e=>e.key==='Enter'&&applyCoupon()} placeholder="Enter coupon code" style={{...inp(couponError),flex:1,padding:'9px 11px',fontSize:13,letterSpacing:1}}/>
                <button onClick={()=>applyCoupon()} disabled={couponLoading} className="btn btn-outline" style={{padding:'9px 14px',fontSize:11,whiteSpace:'nowrap'}}>{couponLoading?'...':'Apply'}</button>
              </div>
              {couponError && <div style={{fontSize:11,color:'#c00',marginBottom:6}}>⚠ {couponError}</div>}
              <button onClick={()=>{ setShowCoupons(v=>!v); if (!showCoupons) loadActiveCoupons(); }} style={{fontSize:11,color:'#7a1f1f',background:'none',border:'none',cursor:'pointer',padding:0,textDecoration:'underline',fontFamily:"'Jost',sans-serif"}}>
                {showCoupons?'▲ Hide coupons':'▼ View available coupons'}
              </button>
              {showCoupons && (
                <div style={{marginTop:10,border:'1px solid var(--border)',background:'#fdf8f0'}}>
                  {couponsLoading ? (
                    <div style={{padding:'14px',textAlign:'center',fontSize:12,color:'#8a7968',fontFamily:"'Jost',sans-serif"}}>Loading available coupons…</div>
                  ) : activeCoupons.length === 0 ? (
                    <div style={{padding:'14px',textAlign:'center',fontSize:12,color:'#8a7968',fontFamily:"'Jost',sans-serif"}}>No active coupons at the moment.</div>
                  ) : activeCoupons.map(cp => (
                    <div key={cp.code} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 12px',borderBottom:'1px solid #f0e8e0'}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontFamily:"'Jost',sans-serif",fontSize:12,fontWeight:700,letterSpacing:1,color:'#7a1f1f'}}>{cp.code}</div>
                        <div style={{fontSize:11,color:'#8a7968',marginTop:2}}>{cp.desc}</div>
                        {cp.min_order > 0 && <div style={{fontSize:10,color:'#aaa',marginTop:1}}>Min order: ₹{cp.min_order.toLocaleString('en-IN')}</div>}
                      </div>
                      <button onClick={()=>applyCoupon(cp.code)} style={{fontSize:11,background:'#7a1f1f',color:'#fff',border:'none',cursor:'pointer',padding:'6px 12px',fontFamily:"'Jost',sans-serif",flexShrink:0,marginLeft:10}}>Apply</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Price breakdown */}
      <div style={{borderTop:'1px solid var(--border)',paddingTop:14}}>
        {[
          ['Subtotal',                 `₹${subtotal.toLocaleString('en-IN',{maximumFractionDigits:0})}`],
          couponData && ['Coupon Discount', `-₹${couponDiscount.toLocaleString('en-IN',{maximumFractionDigits:0})}`, '#2e7d32'],
          ['Shipping',                 shipping===0?(freeShip?'Free (Coupon) 🎉':'Free 🎉'):`₹${shipping}`, shipping===0?'#2e7d32':undefined],
        ].filter(Boolean).map(([k,v,color]) => (
          <div key={k} style={{display:'flex',justifyContent:'space-between',fontSize:13,color:color||'#5a3a3a',marginBottom:10,fontFamily:"'Jost',sans-serif"}}>
            <span>{k}</span><span style={color?{color,fontWeight:600}:{}}>{v}</span>
          </div>
        ))}
        {subtotal < 999 && !freeShip && (
          <div style={{fontSize:11,color:'#e65100',marginBottom:10,fontFamily:"'Jost',sans-serif"}}>
            Add ₹{Math.ceil(999-afterCoupon)} more for free shipping
          </div>
        )}
        <div style={{display:'flex',justifyContent:'space-between',borderTop:'2px solid var(--border)',paddingTop:14,fontFamily:"'Cormorant Garamond',serif",fontSize:21,color:'#7a1f1f',fontWeight:600}}>
          <span>Total</span><span>₹{grandTotal.toLocaleString('en-IN',{maximumFractionDigits:0})}</span>
        </div>
        {couponData && couponDiscount>0 && (
          <div style={{textAlign:'center',marginTop:8,fontSize:11,color:'#2e7d32',fontFamily:"'Jost',sans-serif",fontWeight:600}}>
            🎉 You're saving ₹{couponDiscount.toLocaleString('en-IN',{maximumFractionDigits:0})} on this order!
          </div>
        )}
      </div>

      {/* Security badges */}
      <div style={{marginTop:16,textAlign:'center'}}>
        <div style={{fontSize:11,color:'#8a7968',marginBottom:6}}>🔒 256-bit SSL Secured Checkout</div>
        <div style={{display:'flex',justifyContent:'center',gap:8,flexWrap:'wrap'}}>
          {['💳 Cards','📱 UPI','🏦 Net Banking','📦 COD'].map(b=><span key={b} style={{fontSize:10,color:'#aaa',background:'#f7f0e6',padding:'2px 8px',border:'1px solid #e8ddd0'}}>{b}</span>)}
        </div>
      </div>
    </div>
  );

  return (
    <div className="page">
      <div className="container" style={{padding:'36px 24px 72px',maxWidth:1100}}>

        {/* Step indicator */}
        {step < 3 && (
          <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:4,marginBottom:40,flexWrap:'wrap'}}>
            {['Delivery Address','Payment','Confirmation'].map((label,i) => (
              <div key={label} style={{display:'flex',alignItems:'center'}}>
                <div style={{width:32,height:32,borderRadius:'50%',background:step>=i+1?'#7a1f1f':'#e8ddd0',color:step>=i+1?'#fff':'#aaa',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'Jost',sans-serif",fontWeight:700,fontSize:13,flexShrink:0,transition:'background 0.3s'}}>{i+1}</div>
                <span style={{fontSize:11,letterSpacing:1,fontFamily:"'Jost',sans-serif",color:step>=i+1?'#7a1f1f':'#aaa',textTransform:'uppercase',marginLeft:6,transition:'color 0.3s'}} className="hidden-mobile">{label}</span>
                {i<2 && <div style={{width:clamp(30,6,60),height:2,background:step>i+1?'#7a1f1f':'#e8ddd0',margin:'0 10px',transition:'background 0.3s'}}/>}
              </div>
            ))}
          </div>
        )}

        {/* ── SUCCESS ── */}
        {step === 3 && (
          <div style={{textAlign:'center',padding:'60px 0'}}>
            <div style={{fontSize:80,marginBottom:20,animation:'fadeInUp 0.5s ease'}}>🎉</div>
            <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:'clamp(1.8rem,4vw,3rem)',color:'#7a1f1f',fontStyle:'italic',marginBottom:10}}>Order Placed!</h2>
            <p style={{fontFamily:"'Cormorant Garamond',serif",fontSize:18,color:'#5a3a3a',marginBottom:6}}>Thank you for choosing Venkatagiri Handlooms</p>
            <p style={{color:'#8a7968',marginBottom:4}}>Order ID: <strong style={{color:'#7a1f1f'}}>{orderId}</strong></p>
            <p style={{fontSize:13,color:'#8a7968',marginBottom:36}}>Delivery in 5–7 business days. A confirmation has been noted.</p>
            <div style={{display:'flex',gap:14,justifyContent:'center',flexWrap:'wrap'}}>
              <Link to="/orders" className="btn btn-primary">📦 Track Order</Link>
              <Link to="/collections" className="btn btn-outline">Continue Shopping</Link>
            </div>
            <div style={{marginTop:40,padding:24,background:'#f7f0e6',maxWidth:400,margin:'40px auto 0'}}>
              <div style={{fontFamily:"'Jost',sans-serif",fontSize:11,letterSpacing:2,textTransform:'uppercase',color:'#8a7968',marginBottom:12}}>Need Help?</div>
              <a href="https://wa.me/919963686116" target="_blank" rel="noopener noreferrer"
                style={{display:'inline-flex',alignItems:'center',gap:8,background:'#25D366',color:'#fff',padding:'10px 20px',textDecoration:'none',fontFamily:"'Jost',sans-serif",fontSize:12,fontWeight:600}}>
                💬 WhatsApp Support
              </a>
            </div>
          </div>
        )}

        {step < 3 && (
          <div className="checkout-layout" style={{display:'grid',gridTemplateColumns:'1fr 360px',gap:48}}>

            {/* ── LEFT PANEL ── */}
            <div>

              {/* STEP 1 — Address */}
              {step === 1 && (
                <div>
                  <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:'clamp(1.4rem,2.5vw,1.8rem)',color:'#7a1f1f',fontStyle:'italic',marginBottom:6}}>Delivery Address</h2>
                  <p style={{fontSize:12,color:'#8a7968',marginBottom:24,fontFamily:"'Jost',sans-serif"}}>All fields marked * are mandatory</p>

                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
                    {ADDR_FIELDS.map(field => (
                      <div key={field.name} className="form-group" style={field.half ? {} : {gridColumn:'1/-1'}}>
                        <label className="form-label">
                          {field.label} {field.required && <span style={{color:'#c00'}}>*</span>}
                        </label>
                        {field.select ? (
                          <select style={inp(errors[field.name])} value={address[field.name]} onChange={e=>{ setAddress(a=>({...a,[field.name]:e.target.value})); setErrors(er=>({...er,[field.name]:''})); }}>
                            {STATES.map(s => <option key={s}>{s}</option>)}
                          </select>
                        ) : field.multiline ? (
                          <textarea style={{...inp(errors[field.name]),resize:'vertical',minHeight:72}} value={address[field.name]} onChange={e=>{ setAddress(a=>({...a,[field.name]:e.target.value})); setErrors(er=>({...er,[field.name]:''})); }} placeholder={field.placeholder}/>
                        ) : (
                          <input style={inp(errors[field.name])} value={address[field.name]}
                            onChange={e => {
                              let val = e.target.value;
                              if (field.name==='pincode') val = val.replace(/\D/g,'').slice(0,6);
                              if (field.name==='phone')   val = val.replace(/[^\d+\s-]/g,'').slice(0,15);
                              setAddress(a=>({...a,[field.name]:val}));
                              setErrors(er=>({...er,[field.name]:''}));
                            }}
                            placeholder={field.placeholder}
                          />
                        )}
                        <FieldErr name={field.name}/>
                      </div>
                    ))}
                  </div>

                  {/* ── Gift Wrapping ── */}
                  <div style={{marginBottom:20}}>
                    <div className="form-label" style={{marginBottom:10}}>Gift Wrapping (Optional)</div>
                    <div className={`gift-box ${giftWrap?'selected':''}`} onClick={()=>setGiftWrap(g=>!g)}>
                      <div className="gift-box-header">
                        <div style={{width:20,height:20,borderRadius:4,border:`2px solid ${giftWrap?'#7a1f1f':'#bbb'}`,background:giftWrap?'#7a1f1f':'transparent',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                          {giftWrap && <span style={{color:'#fff',fontSize:13,lineHeight:1}}>✓</span>}
                        </div>
                        <div style={{fontSize:28}}>🎁</div>
                        <div>
                          <div style={{fontFamily:"'Jost',sans-serif",fontWeight:600,fontSize:14,color:'#2d1f1f'}}>Gift Wrap this Order — <span style={{color:'#7a1f1f'}}>+₹99</span></div>
                          <div style={{fontSize:12,color:'#8a7968'}}>Premium tissue paper, ribbon & personalized message card</div>
                        </div>
                      </div>
                      {giftWrap && (
                        <div style={{marginTop:12}} onClick={e=>e.stopPropagation()}>
                          <label className="form-label">Gift Message (optional, max 150 chars)</label>
                          <textarea value={giftMessage} onChange={e=>setGiftMessage(e.target.value.slice(0,150))} placeholder="Write a personal message for the recipient…" style={{width:'100%',padding:'10px 12px',border:'1.5px solid var(--border)',fontSize:13,outline:'none',resize:'vertical',minHeight:72,fontFamily:'inherit'}}/>
                          <div style={{fontSize:11,color:'#aaa',textAlign:'right'}}>{giftMessage.length}/150</div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Mobile order summary */}
                  <div className="show-mobile" style={{margin:'24px 0'}}><OrderSummary mobile/></div>

                  <button onClick={handleNext} className="btn btn-primary" style={{width:'100%',justifyContent:'center',padding:'15px',marginTop:8,fontSize:12}}>
                    Continue to Payment →
                  </button>
                </div>
              )}

              {/* STEP 2 — Payment */}
              {step === 2 && (
                <div>
                  <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:'clamp(1.4rem,2.5vw,1.8rem)',color:'#7a1f1f',fontStyle:'italic',marginBottom:6}}>Payment Method</h2>
                  <p style={{fontSize:12,color:'#8a7968',marginBottom:24,fontFamily:"'Jost',sans-serif"}}>Your payment is 100% secure and encrypted</p>

                  {/* Address summary */}
                  <div style={{background:'#f7f0e6',padding:16,marginBottom:24,borderLeft:'3px solid #7a1f1f',position:'relative'}}>
                    <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,letterSpacing:2,textTransform:'uppercase',color:'#8a7968',marginBottom:6}}>Delivering to</div>
                    <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:17,color:'#2d1f1f',fontWeight:600}}>{address.name} — {address.phone}</div>
                    <div style={{fontSize:13,color:'#5a3a3a',marginTop:2}}>{address.address}, {address.city}, {address.state} — {address.pincode}</div>
                    <button onClick={()=>setStep(1)} style={{position:'absolute',top:12,right:12,fontSize:11,color:'#7a1f1f',background:'none',border:'none',cursor:'pointer',textDecoration:'underline',fontFamily:"'Jost',sans-serif"}}>Change</button>
                  </div>

                  {/* Payment options */}
                  <div style={{marginBottom:24}}>
                    {[
                      { id:'RAZORPAY', icon:'💳', label:'Pay Online (Razorpay)', desc:'Cards, UPI, Net Banking, Wallets — all in one', badge:'RECOMMENDED' },
                      { id:'UPI',      icon:'📱', label:'UPI / QR Code',         desc:'GooglePay, PhonePe, Paytm, BHIM' },
                      { id:'COD',      icon:'📦', label:'Cash on Delivery',       desc:'Pay when your saree arrives at your door', badge:'₹50 extra' },
                    ].map(p => (
                      <div key={p.id} onClick={()=>setPayment(p.id)}
                        style={{padding:'16px 18px',marginBottom:10,cursor:'pointer',transition:'all 0.2s',display:'flex',alignItems:'center',gap:14,border:payment===p.id?'2px solid #7a1f1f':'2px solid var(--border)',background:payment===p.id?'#fdf3f0':'#fff',position:'relative'}}>
                        {p.badge && <div style={{position:'absolute',top:-1,right:10,background:p.id==='RAZORPAY'?'#2e7d32':'#8a7968',color:'#fff',fontSize:9,fontWeight:700,padding:'2px 8px',letterSpacing:1}}>{p.badge}</div>}
                        <div style={{width:18,height:18,borderRadius:'50%',border:`2px solid ${payment===p.id?'#7a1f1f':'#bbb'}`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                          {payment===p.id && <div style={{width:9,height:9,borderRadius:'50%',background:'#7a1f1f'}}/>}
                        </div>
                        <div style={{fontSize:22,flexShrink:0}}>{p.icon}</div>
                        <div>
                          <div style={{fontFamily:"'Jost',sans-serif",fontWeight:600,fontSize:14,color:'#2d1f1f'}}>{p.label}</div>
                          <div style={{fontSize:12,color:'#8a7968'}}>{p.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Online payment logos */}
                  {(payment==='RAZORPAY'||payment==='UPI') && (
                    <div style={{background:'#f7f0e6',padding:12,marginBottom:24,textAlign:'center'}}>
                      <div style={{fontSize:11,color:'#8a7968',marginBottom:8,fontFamily:"'Jost',sans-serif"}}>Accepted payment methods</div>
                      <div style={{display:'flex',justifyContent:'center',gap:10,flexWrap:'wrap'}}>
                        {['VISA','Mastercard','RuPay','UPI','PhonePe','GPay','Paytm','BHIM'].map(m=>(
                          <span key={m} style={{fontSize:11,background:'#fff',border:'1px solid #e8ddd0',padding:'4px 10px',color:'#5a3a3a',fontFamily:"'Jost',sans-serif",fontWeight:500}}>{m}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Mobile summary */}
                  <div className="show-mobile" style={{margin:'20px 0'}}><OrderSummary mobile/></div>

                  <div style={{display:'flex',gap:10}}>
                    <button onClick={()=>setStep(1)} className="btn btn-outline" style={{flex:1,justifyContent:'center'}}>← Back</button>
                    <button onClick={handlePlaceOrder} disabled={loading} className="btn btn-primary" style={{flex:2,justifyContent:'center',padding:'15px',fontSize:12}}>
                      {loading ? '⏳ Processing...' : payment==='RAZORPAY'||payment==='UPI' ? `💳 Pay ₹${grandTotal.toLocaleString('en-IN',{maximumFractionDigits:0})}` : '✅ Place Order'}
                    </button>
                  </div>

                  <div style={{textAlign:'center',marginTop:14,fontSize:11,color:'#8a7968',fontFamily:"'Jost',sans-serif"}}>
                    🔒 Powered by Razorpay · 256-bit SSL · PCI DSS Compliant
                  </div>
                </div>
              )}
            </div>

            {/* ── RIGHT — Order Summary (desktop) ── */}
            <div className="hidden-mobile"><OrderSummary/></div>
          </div>
        )}
      </div>
    </div>
  );
}

function clamp(val, min, max) { return Math.min(Math.max(val, min), max); }
